'use server'

import { apiRequest, ApiResponse } from '../request'

export type NotificationType =
    | 'INFO'
    | 'SUCCESS'
    | 'WARNING'
    | 'ERROR'
    | 'SYSTEM'
    | 'ANNOUNCEMENT'

export type NotificationTargetType =
    | 'ALL'
    | 'MASTER'
    | 'ROLE'
    | 'COMPANY'
    | 'USER'
    | 'EMPLOYEES'
    | 'PROFESSIONALS'
    | 'CLIENTS'
    | 'PROFESSIONAL'
    | 'CLIENT'

export interface NotificationSender {
    id: number
    name: string
}

export interface Notification {
    id: number
    title: string
    message: string
    type: NotificationType
    targetType: NotificationTargetType
    link?: string
    icon?: string
    metadata?: Record<string, any>
    sender?: NotificationSender
    createdAt: string
    expiresAt?: string
    readAt?: string
    isRead: boolean
    /** Só no evento `notification:new` do socket. */
    scope?: 'COMPANY' | 'PERSONAL'
    alertKind?: string
}

export interface NotificationListResponse {
    notifications: Notification[]
    total: number
    unreadCount: number
    page: number
    totalPages: number
}

export interface CreateNotificationData {
    title: string
    message: string
    type?: NotificationType
    targetType: NotificationTargetType
    targetRole?: string
    targetCompanyId?: number
    targetUserId?: number
    targetProfessionalId?: number
    targetClientId?: number
    link?: string
    icon?: string
    metadata?: Record<string, any>
    expiresAt?: string
}

export interface NotificationQuery {
    unreadOnly?: boolean
    type?: NotificationType
    page?: number
    limit?: number
    /** Categoria (módulo). Use OTHER_CATEGORY_KEY para as sem módulo. */
    module?: string
    /** Subcategoria (recurso). */
    resource?: string
    /** Título exato — é assim que se expande um grupo. */
    title?: string
    /** `COMPANY` (sino) ou `PERSONAL` ("Meus Alertas"). Ver `NotificationScope`. */
    scope?: 'COMPANY' | 'PERSONAL'
    /** Busca por trecho do título do evento (aplicada no servidor). */
    search?: string
}

/**
 * Categoria das notificações sem módulo. Precisa ser um valor sentinela e não
 * string vazia: o backend distingue "sem filtro" de "filtrar pelas sem módulo".
 */
export const OTHER_CATEGORY_KEY = '__other__'

/**
 * Um grupo colapsa todas as notificações de mesmo (módulo, recurso, título) —
 * as 53 "Nova Cobrança Criada" viram uma linha. Os três campos identificam o
 * grupo nas ações em lote e na expansão.
 */
export interface NotificationGroup {
    module?: string
    resource?: string
    title: string
    total: number
    unread: number
    type: NotificationType
    lastAt: string
    lastMessage?: string
    lastLink?: string
    icon?: string
}

export interface NotificationGroupListResponse {
    groups: NotificationGroup[]
    /** Total de GRUPOS — a paginação é por grupo, não por notificação. */
    total: number
    unreadCount: number
    page: number
    totalPages: number
}

export interface NotificationGroupKey {
    module?: string
    resource?: string
    title: string
}

export interface NotificationCategoryResource {
    key: string
    label: string
    total: number
    unread: number
}

export interface NotificationCategory {
    key: string
    label: string
    total: number
    unread: number
    resources: NotificationCategoryResource[]
}

export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
    INFO: 'Informação',
    SUCCESS: 'Sucesso',
    WARNING: 'Aviso',
    ERROR: 'Erro',
    SYSTEM: 'Sistema',
    ANNOUNCEMENT: 'Anuncio',
}

export const NOTIFICATION_TYPE_COLORS: Record<NotificationType, string> = {
    INFO: 'badge-info',
    SUCCESS: 'badge-success',
    WARNING: 'badge-warning',
    ERROR: 'badge-error',
    SYSTEM: 'badge-secondary',
    ANNOUNCEMENT: 'badge-secondary',
}

export const NOTIFICATION_TARGET_LABELS: Record<NotificationTargetType, string> =
{
    ALL: 'Todos os Usuários',
    MASTER: 'Administradores Master',
    ROLE: 'Perfil Especifico',
    COMPANY: 'Empresa Especifica',
    USER: 'Usuário Especifico',
    EMPLOYEES: 'Todos os Funcionários',
    PROFESSIONALS: 'Todos os Profissionais',
    CLIENTS: 'Todos os Pacientes/Clientes',
    PROFESSIONAL: 'Profissional Especifico',
    CLIENT: 'Paciente/Cliente Especifico',
}

// Labels para empresa (sem opcoes de master)
export const COMPANY_TARGET_LABELS: Record<string, string> = {
    EMPLOYEES: 'Todos os Funcionários',
    PROFESSIONALS: 'Todos os Profissionais',
    CLIENTS: 'Todos os Pacientes/Clientes',
    PROFESSIONAL: 'Profissional Especifico',
    CLIENT: 'Paciente/Cliente Especifico',
}

export async function getNotifications(
    query?: NotificationQuery
): Promise<ApiResponse<NotificationListResponse>> {
    return apiRequest<NotificationListResponse>('notifications', {
        params: query,
    })
}

/**
 * `COMPANY` = o sino de sempre (operação da empresa).
 * `PERSONAL` = "Meus Alertas": o que é endereçado a esta pessoa.
 *
 * Sem o filtro vêm os dois — e foi essa mistura que tornou o sino inútil: com
 * dezenas de avisos de operação por hora, ninguém achava os próprios.
 */
export type NotificationScope = 'COMPANY' | 'PERSONAL'

export async function getUnreadCount(
    scope?: NotificationScope
): Promise<ApiResponse<{ count: number }>> {
    return apiRequest<{ count: number }>('notifications/unread-count', {
        params: scope ? { scope } : undefined,
    })
}

/**
 * Lista agrupada por tipo de evento. A paginação vem do servidor por GRUPO —
 * agrupar no cliente colapsaria só as 20 linhas da página atual e mentiria sobre
 * quantas existem no total.
 */
export async function getNotificationGroups(
    query?: NotificationQuery
): Promise<ApiResponse<NotificationGroupListResponse>> {
    return apiRequest<NotificationGroupListResponse>('notifications/grouped', {
        params: query,
    })
}

/** Categorias e subcategorias que o usuário enxerga, com contadores. */
export async function getNotificationCategories(): Promise<
    ApiResponse<NotificationCategory[]>
> {
    return apiRequest<NotificationCategory[]>('notifications/categories', {})
}

export async function markGroupAsRead(
    group: NotificationGroupKey
): Promise<ApiResponse<{ affected: number }>> {
    return apiRequest<{ affected: number }>(
        'notifications/group/read',
        { data: group },
        'patch'
    )
}

export async function deleteNotificationGroup(
    group: NotificationGroupKey
): Promise<ApiResponse<{ affected: number }>> {
    return apiRequest<{ affected: number }>(
        'notifications/group/delete',
        { data: group },
        'post'
    )
}

export async function createNotification(
    data: CreateNotificationData
): Promise<ApiResponse<Notification>> {
    return apiRequest<Notification>('notifications', { data }, 'post')
}

export async function markAsRead(
    notificationId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `notifications/${notificationId}/read`,
        {},
        'patch'
    )
}

export async function markAllAsRead(): Promise<ApiResponse<void>> {
    return apiRequest<void>('notifications/read-all', {}, 'patch')
}

export async function deleteNotification(
    notificationId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`notifications/${notificationId}`, {}, 'delete')
}

export async function deleteAllNotifications(): Promise<ApiResponse<void>> {
    return apiRequest<void>('notifications', {}, 'delete')
}

/** Um tipo de alerta de "Meus Alertas" e se a pessoa o recebe. */
export interface PersonalAlertPreference {
    key: string
    label: string
    description: string
    /** Recurso do menu de que o alerta depende; `null` = vale para todos. */
    module: string | null
    resource: string | null
    enabled: boolean
}

export async function getPersonalAlertPreferences(): Promise<ApiResponse<PersonalAlertPreference[]>> {
    return apiRequest<PersonalAlertPreference[]>('notifications/personal-preferences', {})
}

/** Grava os tipos DESLIGADOS; os demais continuam chegando. */
export async function updatePersonalAlertPreferences(
    mutedKinds: string[]
): Promise<ApiResponse<PersonalAlertPreference[]>> {
    return apiRequest<PersonalAlertPreference[]>(
        'notifications/personal-preferences',
        { data: { mutedKinds } },
        'put'
    )
}

/** Som de mensagem nova em "Meus Alertas". Sem escolha gravada, vem ligado. */
export async function getPersonalAlertSound(): Promise<ApiResponse<{ enabled: boolean }>> {
    return apiRequest<{ enabled: boolean }>('notifications/personal-sound', {})
}

export async function updatePersonalAlertSound(enabled: boolean): Promise<ApiResponse<{ enabled: boolean }>> {
    return apiRequest<{ enabled: boolean }>('notifications/personal-sound', { data: { enabled } }, 'put')
}
